import React, { useState, useRef, useEffect } from "react";
import { Send, Bot, User, Loader2, Trash2, Phone } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChatbotTester = ({ authAxios }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [phone, setPhone] = useState("9930001848");
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    setMessages(prev => [...prev, { role: "user", text, time: new Date().toLocaleTimeString() }]);
    setInput("");
    setSending(true);

    try {
      const res = await authAxios.post(`${API_URL}/api/admin/whatsapp/test-chatbot`, {
        phone,
        message: text,
      });
      setMessages(prev => [...prev, { role: "bot", text: res.data.reply, time: new Date().toLocaleTimeString() }]);
    } catch {
      setMessages(prev => [...prev, { role: "bot", text: "Error getting response. Check backend logs.", time: new Date().toLocaleTimeString(), error: true }]);
    }
    setSending(false);
  };

  const clearChat = () => {
    setMessages([]);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="chatbot-tester">
      {/* Header */}
      <div className="bg-[#075E54] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#25D366] flex items-center justify-center">
            <Bot size={18} className="text-white" />
          </div>
          <div>
            <p className="text-white font-semibold text-sm">DoozyGo AI Chatbot</p>
            <p className="text-[#80cbc4] text-xs">Test Mode — Simulates customer WhatsApp</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-[#064E45] rounded-lg px-2 py-1">
            <Phone size={12} className="text-[#80cbc4]" />
            <input
              value={phone}
              onChange={e => setPhone(e.target.value.replace(/\D/g, ""))}
              className="bg-transparent text-white text-xs w-24 outline-none placeholder:text-[#80cbc4]"
              placeholder="Phone"
              data-testid="chatbot-phone-input"
            />
          </div>
          <button onClick={clearChat} className="p-1.5 hover:bg-[#064E45] rounded-lg" title="Clear chat">
            <Trash2 size={14} className="text-[#80cbc4]" />
          </button>
        </div>
      </div>

      {/* Chat Body */}
      <div
        className="h-80 overflow-y-auto p-4 space-y-3"
        style={{ backgroundImage: "url('data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" viewBox=\"0 0 60 60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cg fill=\"none\" fill-rule=\"evenodd\"%3E%3Cg fill=\"%23e5ddd5\" fill-opacity=\"0.3\"%3E%3Cpath d=\"M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')", backgroundColor: "#ECE5DD" }}
        data-testid="chatbot-messages"
      >
        {messages.length === 0 && (
          <div className="text-center py-8">
            <Bot size={40} className="mx-auto text-[#94a3b8] mb-3" />
            <p className="text-sm text-[#94a3b8]">Send a message to test the AI chatbot</p>
            <div className="flex flex-wrap gap-2 justify-center mt-4">
              {["Hi! What packages do you have?", "Birthday party setup karna hai", "Is April 5th available in Bandra?", "What's your cheapest package?"].map(q => (
                <button
                  key={q}
                  onClick={() => { setInput(q); }}
                  className="px-3 py-1.5 bg-white rounded-full text-xs text-[#475569] hover:bg-[#25D366] hover:text-white transition-colors shadow-sm"
                  data-testid="quick-prompt"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={msg.time ? `${msg.role}-${msg.time}-${i}` : `msg-${i}`} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
              msg.role === "user"
                ? "bg-[#DCF8C6] text-[#1E1B4B] rounded-tr-sm"
                : msg.error
                  ? "bg-red-50 text-red-700 rounded-tl-sm"
                  : "bg-white text-[#1E1B4B] rounded-tl-sm"
            }`}>
              <p className="whitespace-pre-wrap leading-relaxed">{msg.text}</p>
              <p className={`text-[10px] mt-1 text-right ${msg.role === "user" ? "text-[#7cb342]" : "text-[#94a3b8]"}`}>{msg.time}</p>
            </div>
          </div>
        ))}

        {sending && (
          <div className="flex justify-start">
            <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex items-center gap-2">
              <Loader2 size={14} className="animate-spin text-[#25D366]" />
              <span className="text-xs text-[#94a3b8]">Typing...</span>
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 bg-[#F0F0F0] flex items-center gap-2">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()}
          placeholder="Type a message as customer..."
          className="flex-1 px-4 py-2.5 rounded-full bg-white text-sm outline-none focus:ring-2 focus:ring-[#25D366]/30"
          disabled={sending}
          data-testid="chatbot-input"
        />
        <button
          onClick={sendMessage}
          disabled={sending || !input.trim()}
          className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center text-white hover:bg-[#1da851] disabled:opacity-40 transition-colors shrink-0"
          data-testid="chatbot-send-btn"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
};

export default ChatbotTester;
